import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Trans } from "react-i18next";

import { client } from "../../bit/board/hooks/useGraphQLClient";
import { MUTATION_SWITCH_SHELLY_CONTROLLER } from "../../config/graphql/mutation";
import { useCallSounds } from "../../lib/hooks/useCallSounds";

import Modal from "../../components/Modal";

const Bell = React.memo(() => {
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const { startSound, ringingSound, endSound, playSound, stopAllSounds } =
    useCallSounds();

  const timeoutSeconds = useMemo(() => {
    const timeout = searchParams.get("timeout");
    return timeout ? parseInt(timeout, 10) : 7;
  }, [searchParams]);

  const [status, setStatus] = useState<"loading" | "success" | "error">();
  const timeoutRef = React.useRef<NodeJS.Timeout>();

  const onDismiss = useCallback(() => {
    stopAllSounds();
    setStatus(undefined);
    navigate("/", { replace: true });
  }, [navigate, stopAllSounds]);

  const timeout = useMemo(() => {
    if (status === "error") {
      return 5000;
    }
    if (status === "success") {
      return timeoutSeconds * 1000;
    }
    return 0;
  }, [status, timeoutSeconds]);

  useEffect(() => {
    const onSwitch = async () => {
      if (!id) {
        navigate("/", { replace: true });
        return;
      }

      setStatus("loading");
      playSound(startSound);

      try {
        await client.request(MUTATION_SWITCH_SHELLY_CONTROLLER, {
          input: { id, action: "ON_WITH_DELAY" },
        });

        playSound(ringingSound);
        setStatus("success");
      } catch (error) {
        playSound(endSound);
        setStatus("error");
      }
    };

    onSwitch();

    return () => {
      stopAllSounds();
      clearTimeout(timeoutRef.current);
    };
  }, [id]);

  useEffect(() => {
    if (timeout > 0) {
      timeoutRef.current = setTimeout(() => {
        playSound(endSound);
        onDismiss();
      }, timeout);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [timeout, onDismiss, endSound, playSound]);

  if (!status) {
    return null;
  }

  return (
    <Modal
      status={status}
      title={<Trans i18nKey={`bell:status.${status}.title`} />}
      message={<Trans i18nKey={`bell:status.${status}.message`} />}
      onDismiss={onDismiss}
    />
  );
});

export default Bell;
